import { useEffect, useRef } from 'react';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { BUSINESS } from '@jira/servicedesk__onboarding-checklist/src/business.constants.tsx';
import { useJsmOnboardingType } from '@jira/servicedesk__onboarding-checklist/src/useJsmOnboardingType.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import {
	shouldRenderRequestTypesTableViewSettings,
	shouldRouteShowQuickstartOnboarding,
} from '../../common/utils.tsx';

export type Preloadable = { preload?: () => void };

/**
 * Eagerly kicks off the JSM onboarding-type fetch and preloads the lazy
 * right-sidebar chunk so both run in parallel. Returns whether the sidebar
 * should be rendered for the current route + onboarding type.
 *
 * Must be imported synchronously by the layout entry — wrapping it in
 * `React.lazy` defeats the parallelism and shows the chunk as the last UFO
 * hold.
 */
export const useJsmRightSidebarsPreload = (target: Preloadable): boolean => {
	const hasPreloadedRef = useRef(false);
	const projectContext = useProjectContext();
	const projectContextData = projectContext?.data;
	const route = useCurrentRoute();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [fetchOnboardingType, projectContextData]);

	const shouldRender =
		(jsmOnboardingData?.onboardingType === BUSINESS &&
			shouldRouteShowQuickstartOnboarding(route.name)) ||
		shouldRenderRequestTypesTableViewSettings(route.name);

	useEffect(() => {
		if (shouldRender && !hasPreloadedRef.current) {
			target.preload?.();
			hasPreloadedRef.current = true;
		}
	}, [shouldRender, target]);

	return shouldRender;
};
